import { useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { isCountryAllowed } from '../utils/access';

// ── Minimal ambient background ───────────────────────────────────────────────
// Clean, Apple-like: the plain page colour with two very soft radial glows for
// gentle depth. No data-viz clutter — lets the content breathe.
function ChartBg({ dark }) {
  const t = '#1DB89A';   // teal accent, kept extremely subtle

  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
      <svg width="100%" height="100%" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice">
        <defs>
          {/* Soft teal glow, upper area */}
          <radialGradient id="cv-glow-a" cx="50%" cy="18%" r="55%">
            <stop offset="0%"   stopColor={t} stopOpacity={dark ? 0.10 : 0.06} />
            <stop offset="100%" stopColor={t} stopOpacity="0" />
          </radialGradient>
          {/* Cool neutral glow, lower-left, for a hint of dimension */}
          <radialGradient id="cv-glow-b" cx="18%" cy="88%" r="50%">
            <stop offset="0%"   stopColor="#8ea3c2" stopOpacity={dark ? 0.07 : 0.05} />
            <stop offset="100%" stopColor="#8ea3c2" stopOpacity="0" />
          </radialGradient>
        </defs>

        <rect width="1440" height="900" fill="url(#cv-glow-a)" />
        <rect width="1440" height="900" fill="url(#cv-glow-b)" />
      </svg>
    </div>
  );
}

// ── Data ──────────────────────────────────────────────────────────────────────
const COUNTRIES = [
  { code: 'uk',  name: 'United Kingdom', accent: '#1DB89A', status: 'active',
    description: 'Mobile app analytics across ride hailing, food delivery, and more — powered by the UK\'s largest behavioural panel.' },
  { code: 'us',  name: 'United States',  accent: '#6366f1', status: 'locked',
    description: 'US consumer app intelligence across top categories. Population-scale signals from America\'s digital economy.' },
  { code: 'th',  name: 'Thailand',       accent: '#f59e0b', status: 'active',
    description: 'Southeast Asia market intelligence for Thailand\'s fast-growing digital app ecosystem.' },
  { code: 'ksa', name: 'Saudi Arabia',   accent: '#10b981', status: 'locked',
    description: 'GCC market intelligence covering Saudi Arabia\'s rapidly expanding mobile-first consumer market.' },
];

function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <rect x="3" y="11" width="18" height="11" rx="2"/>
      <path d="M7 11V7a5 5 0 0110 0v4"/>
    </svg>
  );
}

function CountryCard({ country, onSelect, dark, restricted }) {
  const isActive = country.status === 'active' && !restricted;
  const { accent } = country;

  const cardBg = dark
    ? 'linear-gradient(145deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.030) 50%, rgba(122,141,168,0.04) 100%)'
    : 'linear-gradient(145deg, rgba(255,255,255,0.78) 0%, rgba(244,246,250,0.65) 55%, rgba(232,236,244,0.55) 100%)';
  const cardBorder = dark
    ? `1px solid rgba(150,165,190,${isActive ? '0.14' : '0.07'})`
    : `1px solid rgba(122,141,168,${isActive ? '0.22' : '0.12'})`;
  const restShadow = dark
    ? 'inset 0 1px 0 rgba(255,255,255,0.08), 0 10px 32px rgba(0,0,0,0.32)'
    : 'inset 0 1px 0 rgba(255,255,255,0.90), 0 10px 36px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)';
  const hoverShadow = dark
    ? `inset 0 1px 0 rgba(255,255,255,0.12), 0 24px 64px rgba(0,0,0,0.44), 0 0 0 1px ${accent}28`
    : `inset 0 1px 0 rgba(255,255,255,0.95), 0 22px 56px rgba(0,0,0,0.12), 0 0 0 1px ${accent}38`;
  const titleColor = dark ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.88)';
  const descColor  = dark ? 'rgba(255,255,255,0.48)' : 'rgba(0,0,0,0.52)';
  const lockedColor = dark ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.30)';
  const lockedBadgeBg = dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
  const lockedBadgeBorder = dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)';

  return (
    <div
      onClick={() => isActive && onSelect(country.code)}
      style={{
        background: cardBg,
        backdropFilter: 'blur(36px) saturate(200%)',
        WebkitBackdropFilter: 'blur(36px) saturate(200%)',
        border: cardBorder,
        borderRadius: 24,
        padding: '26px 26px 24px',
        cursor: isActive ? 'pointer' : 'default',
        position: 'relative', overflow: 'hidden',
        boxShadow: restShadow,
        transition: 'box-shadow 0.35s cubic-bezier(0.22,1,0.36,1), transform 0.35s cubic-bezier(0.22,1,0.36,1)',
        opacity: isActive ? 1 : 0.65,
        display: 'flex', flexDirection: 'column', gap: 0,
      }}
      onMouseEnter={e => {
        if (!isActive) return;
        e.currentTarget.style.boxShadow = hoverShadow;
        e.currentTarget.style.transform = 'translateY(-4px) scale(1.01)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = restShadow;
        e.currentTarget.style.transform = 'translateY(0) scale(1)';
      }}
    >
      {/* Glass sheen — diagonal light catch across the top */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '55%',
        background: dark
          ? 'linear-gradient(172deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.012) 45%, transparent 100%)'
          : 'linear-gradient(172deg, rgba(255,255,255,0.80) 0%, rgba(255,255,255,0.18) 45%, transparent 100%)',
        pointerEvents: 'none',
      }} />

      {/* Subtle accent glow — top-right corner only */}
      <div style={{
        position: 'absolute', top: -24, right: -24,
        width: 160, height: 160,
        background: `radial-gradient(circle, ${accent}${dark ? '12' : '0c'} 0%, transparent 65%)`,
        pointerEvents: 'none',
      }} />

      {/* Top row: country name + status badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', color: titleColor }}>
          {country.name}
        </div>
        {isActive ? (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
            fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 100,
            background: `${accent}18`, border: `1px solid ${accent}38`, color: accent,
          }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: accent }} />
            Live
          </span>
        ) : (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
            fontSize: 11, fontWeight: 500, padding: '4px 10px', borderRadius: 100,
            background: lockedBadgeBg,
            border: `1px solid ${lockedBadgeBorder}`,
            color: lockedColor,
          }}>
            <LockIcon />
            {restricted ? 'Restricted' : 'Coming soon'}
          </span>
        )}
      </div>

      {/* Description */}
      <p style={{ fontSize: 13, color: descColor, lineHeight: 1.6, margin: '0 0 22px', flexGrow: 1 }}>
        {country.description}
      </p>

      {/* CTA */}
      {isActive ? (
        <button
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13, fontWeight: 600, color: '#fff',
            background: 'linear-gradient(145deg, rgba(29,184,154,0.85) 0%, rgba(14,154,126,0.70) 100%)',
            backdropFilter: 'blur(16px) saturate(180%)',
            WebkitBackdropFilter: 'blur(16px) saturate(180%)',
            border: '1px solid rgba(255,255,255,0.28)',
            borderRadius: 100, padding: '9px 20px',
            cursor: 'pointer', alignSelf: 'flex-start',
            boxShadow: '0 8px 24px rgba(29,184,154,0.35), inset 0 1px 0 rgba(255,255,255,0.35)',
            transition: 'transform 0.25s cubic-bezier(0.22,1,0.36,1), box-shadow 0.25s cubic-bezier(0.22,1,0.36,1)',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 14px 34px rgba(29,184,154,0.45), inset 0 1px 0 rgba(255,255,255,0.45)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(29,184,154,0.35), inset 0 1px 0 rgba(255,255,255,0.35)';
          }}
          onClick={e => { e.stopPropagation(); onSelect(country.code); }}
        >
          Explore dashboard
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
          </svg>
        </button>
      ) : (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 13, fontWeight: 500, color: lockedColor, alignSelf: 'flex-start',
        }}>
          <LockIcon />
          {restricted ? 'Contact Eureka for access' : 'Notify me when available'}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function CountryView({ onSelect, onHome }) {
  const { dark, toggle } = useContext(ThemeContext);
  const { allowedCountries } = useAuth();

  const pageBg    = dark ? '#0d1117' : '#f0f2f6';
  // Glassy ribbon: left edge teal-tinted → centre grey → right edge slate
  const navBg     = dark
    ? 'linear-gradient(90deg, rgba(29,184,154,0.06) 0%, rgba(15,19,28,0.84) 25%, rgba(15,19,28,0.84) 75%, rgba(107,127,163,0.08) 100%)'
    : 'linear-gradient(90deg, rgba(29,184,154,0.05) 0%, rgba(244,246,250,0.88) 22%, rgba(244,246,250,0.88) 78%, rgba(122,141,168,0.06) 100%)';
  const navBorder = dark
    ? '1px solid rgba(122,141,168,0.12)'
    : '1px solid rgba(122,141,168,0.14)';
  const btnStyle  = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    height: 32, fontSize: 13, fontWeight: 500,
    color: dark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.58)',
    background: dark
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(255,255,255,0.72)',
    backdropFilter: 'blur(20px) saturate(160%)',
    WebkitBackdropFilter: 'blur(20px) saturate(160%)',
    border: dark ? '1px solid rgba(122,141,168,0.18)' : '1px solid rgba(122,141,168,0.22)',
    borderRadius: 100, padding: '0 16px', cursor: 'pointer',
    boxShadow: dark
      ? 'inset 0 1px 0 rgba(255,255,255,0.07), 0 4px 14px rgba(0,0,0,0.30)'
      : 'inset 0 1px 0 rgba(255,255,255,0.90), 0 4px 14px rgba(0,0,0,0.08)',
    transition: 'transform 0.25s cubic-bezier(0.22,1,0.36,1), box-shadow 0.25s cubic-bezier(0.22,1,0.36,1)',
  };
  const btnLift = {
    onMouseEnter: e => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = dark
        ? 'inset 0 1px 0 rgba(255,255,255,0.10), 0 10px 24px rgba(0,0,0,0.38)'
        : 'inset 0 1px 0 rgba(255,255,255,0.95), 0 10px 24px rgba(0,0,0,0.12)';
    },
    onMouseLeave: e => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = btnStyle.boxShadow;
    },
  };
  const headingColor = dark ? 'rgba(255,255,255,0.92)' : 'rgba(15,20,30,0.88)';
  const subColor     = dark ? 'rgba(255,255,255,0.46)' : 'rgba(15,20,30,0.52)';

  return (
    <div style={{
      minHeight: '100vh',
      background: pageBg,
      display: 'flex', flexDirection: 'column',
      fontFamily: 'Inter, -apple-system, sans-serif',
      position: 'relative',
    }}>
      <ChartBg dark={dark} />

      {/* ── Glassy nav ── */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        padding: '0 32px', height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: navBg,
        backdropFilter: 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        borderBottom: navBorder,
      }}>
        <img src="/eureka-logo.png" alt="Eureka AI" style={{ height: 36, display: 'block' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={toggle} title={dark ? 'Light mode' : 'Dark mode'} style={btnStyle} {...btnLift}>
            {dark ? '☀️' : '🌙'}
          </button>
          <button onClick={onHome} style={btnStyle} {...btnLift}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            Home
          </button>
        </div>
      </nav>

      {/* ── Body ── */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '60px 24px', position: 'relative', zIndex: 1,
      }}>

        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: 52, maxWidth: 560 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
            color: '#1DB89A',
            background: 'rgba(29,184,154,0.10)',
            border: '1px solid rgba(29,184,154,0.22)',
            borderRadius: 100, padding: '5px 16px', marginBottom: 22,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#1DB89A', display: 'inline-block' }} />
            Omni Growth Platform
          </div>

          <h1 style={{
            fontSize: 'clamp(34px, 4.5vw, 52px)', fontWeight: 800,
            letterSpacing: '-0.04em', color: headingColor,
            margin: '0 0 16px', lineHeight: 1.1,
          }}>
            Select a Market
          </h1>
          <p style={{ fontSize: 15, color: subColor, lineHeight: 1.7, margin: 0 }}>
            Population-scale mobile app intelligence built on Byron Sharp's empirical marketing science.
            Choose your market to explore the dashboard.
          </p>
        </div>

        {/* Country grid — 2×2 */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 16, width: '100%', maxWidth: 780,
        }}>
          {COUNTRIES.map(c => (
            <CountryCard
              key={c.code} country={c} onSelect={onSelect} dark={dark}
              restricted={!isCountryAllowed(allowedCountries, c.code)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
